import { List } from 'immutable'
import { getNodeList } from '../node'
import { RAINBOW_CATEGORY_ID_TO_LABEL } from '../content/rainbow-categories'
import { fetchContentIdToTitleMap } from './fetchContentIdToTitleMap'

// Helper: Build listIdToName map for List columns
async function buildListIdToNameMap (pmLists) {
  const listIdToName = {}
  if (pmLists && typeof pmLists.forEach === 'function') {
    pmLists.forEach((list, id) => {
      listIdToName[String(id)] = list.get('adminTitle') || list.get('title') || id
    })
  }
  return listIdToName
}

// Helper: Build contentIdToTitle map for Content/Person columns (GraphQL)
async function buildContentIdToTitleMap (contentIds, graphqlServerUrl, auth) {
  if (contentIds && contentIds.length > 0) {
    return fetchContentIdToTitleMap(contentIds, graphqlServerUrl, auth)
  }
  return {}
}

// Helper: Build nodeIdToTitle map for Has Watched Title column (getNodeList)
async function buildNodeIdToTitleMap (nodeIds, brooklynServerUrl, auth) {
  const nodeIdToTitle = {}
  if (nodeIds && nodeIds.length > 0) {
    const nodes = await getNodeList({
      auth,
      server: brooklynServerUrl,
      nids: List(nodeIds),
    })
    nodes.forEach((node) => {
      const nid = node.get('nid')
      if (nid) {
        nodeIdToTitle[nid.toString()] = node.get('title')
      }
    })
  }
  return nodeIdToTitle
}

// Helper: Build typeIdToType map for Content Reference Types
function buildTypeIdToTypeMap (contentReferenceTypes) {
  const typeIdToType = {}
  if (Array.isArray(contentReferenceTypes)) {
    contentReferenceTypes.forEach((t) => {
      typeIdToType[String(t.id)] = t.type || t.label || t.id
    })
  } else if (contentReferenceTypes && typeof contentReferenceTypes.toJS === 'function') {
    contentReferenceTypes.toJS().forEach((t) => {
      typeIdToType[String(t.id)] = t.type || t.label || t.id
    })
  }
  return typeIdToType
}

function buildRainbowCategoryIdToLabelMap () {
  return RAINBOW_CATEGORY_ID_TO_LABEL
}

/**
 * Build all ID to title mapping objects needed for CSV export.
 * @param {Object} params
 * @param {Array} params.contentIds - Array of content/person IDs to map to titles (GraphQL)
 * @param {Array} params.nodeIds - Array of node IDs to map to titles (getNodeList)
 * @param {Map} params.pmLists - Immutable Map of lists (UUIDs to list objects)
 * @param {string} params.graphqlServerUrl - GraphQL endpoint
 * @param {string} params.brooklynServerUrl - Brooklyn endpoint for getNodeList
 * @param {string|function} params.auth - Auth token or function
 * @param {Array} params.contentReferenceTypes - Array of content reference types
 * @returns {Promise<{listIdToName: Object, contentIdToTitle: Object, nodeIdToTitle: Object, typeIdToType: Object, rainbowCategoryIdToLabel: Object}>}
 */
export async function buildAllIdToTitleMaps ({
  contentIds,
  nodeIds,
  pmLists,
  graphqlServerUrl,
  brooklynServerUrl,
  auth,
  contentReferenceTypes,
}) {
  const listIdToName = await buildListIdToNameMap(pmLists)
  const contentIdToTitle = await buildContentIdToTitleMap(contentIds, graphqlServerUrl, auth)
  const nodeIdToTitle = await buildNodeIdToTitleMap(nodeIds, brooklynServerUrl, auth)
  const typeIdToType = buildTypeIdToTypeMap(contentReferenceTypes)
  const rainbowCategoryIdToLabel = buildRainbowCategoryIdToLabelMap()
  return { listIdToName, contentIdToTitle, nodeIdToTitle, typeIdToType, rainbowCategoryIdToLabel }
}
